import { IEvent, IHost, IStore, IUser } from "@Interfaces";
import { ButtonV2 } from "@Modules/common/ButtonV2";
import { loadStripePaymentGateway } from "repoV2/utils/payment/Stripe/Stripe.utils";
import {
    ACTION_TYPES,
    API_ACTION_TYPES,
    AUTO_DEBIT_PAYMENT_OPTION,
    EVENT_TYPE,
    FETCH_STATUS,
    IAction,
    IFormDetails,
    INDIA,
    MEDIA,
    QUESTION_PLACEMENT,
    REFUND_ICON_SRC,
    SELECTORS,
    chainShortCircuits,
    createAction,
    getUserDetailsFormKey,
    isEmpty,
    isEventFetched,
    isIndia,
    isUserDetailFormKey,
    useFetchStatus,
    useFormDetails,
    useProcessing,
} from "@Utils";
import { EVENT_ENUM } from "@Utils/constants/event";
import { IFetchDataAction } from "@Redux/sagas/api";
import { processCustomQuestions } from "@Utils/customQuestions";
import { HiArrowLeft } from "@react-icons/all-files/hi/HiArrowLeft";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAnalyticsInfo } from "repoV2/utils/common/analytics/analyticsInfo";
import { getUtmParams } from "repoV2/utils/common/analytics/utmParams";
import { classNames } from "repoV2/utils/common/render/classNames";
import { CUSTOM_QUESTION_TYPES } from "repoV2/utils/getInTouchForm/getInTouchForm.constants";
import { getLeadsFormFileUploadSubfolder } from "repoV2/utils/getInTouchForm/getInTouchForm.utils";
import {
    ICountryCodeDropdownOption,
    getCountryCodeDropdownOptionFromKey,
} from "utils/location";
import { isBrowser } from "repoV2/utils/common/render/window";
import { postAnalytics } from "repoV2/features/Common/Analytics/Analytics.utils";
import { ANALYTICS_EVENTS } from "repoV2/features/Common/Analytics/Analytics.constants";
import { checkIsStringEmpty } from "repoV2/utils/common/dataTypes/string";
import useToastMessage, {
    toast_type_keys,
} from "repoV2/components/common/Toast/useToastMessage";
import { NUMBER_REGEX } from "repoV2/features/Common/utils/numbers";
import { useToggleState } from "repoV2/utils/common/dataTypes/boolean";
import { logError } from "repoV2/utils/common/error/error";
import { IAvailabilityApiResponse } from "@Redux/sagas/event";
import { URL_SEARCH_PARAMS } from "repoV2/features/Common/modules/URLsNRouting/URLsNRouting.constants";
import { isAutoDebitActiveOnMainListingData } from "repoV2/features/Listing/Listing.utils";
import {
    FORM_LOG_TYPES,
    GST_STATUS,
} from "repoV2/features/Listing/Listing.constants";
import { getCartData } from "@Modules/eventPage/utils/cart";
import {
    CREDS_ALREADY_EXISTS_CHOICES,
    EXTRA_FIELD,
    EXTRA_VALIDATION,
    FORM_ENUM,
    FORM_FIELDS,
} from "./FormData";
import { IUserDetails } from "./IUserDetails";
import {
    getBaseFormFields,
    isProceedToUpdateAccountDetails,
    isProceedWithExistingAccountDetails,
} from "./UserDetails.Utils";
import { CustomQuestions, DetailsForm, DirectDiscount } from "./components";
import { ICustomQuestions } from "./components/CustomQuestions/ICustomQuestions";
import styles from "./userDetails.module.scss";
import { usePrefillBookingFormValues } from "./utils/usePrefillBookingFormValues";
import useRebookingConfirmationModalContainer from "./components/RebookingConfirmationModal/useRebookingConfirmationModalContainer";
import RebookingConfirmationModal from "./components/RebookingConfirmationModal/RebookingConfirmationModal";
import { useOrderBumps } from "./components/OrderBumps/useOrderBumps";
import { OrderBumps } from "./components/OrderBumps";
import { TotalPayableStrip } from "./components/TotalPayableStrip";
import ConfirmCredsChange from "./modules/ConfirmCredsChange/ConfirmCredsChange";
import { IConfirmCredsChange } from "./modules/ConfirmCredsChange/ConfirmCredsChange.interfaces";
import AccountUpdateOtpVerification from "./modules/AccountUpdateOtpVerification/AccountUpdateOtpVerification";
import { IAccountUpdateOtpVerification } from "./modules/AccountUpdateOtpVerification/AccountUpdateOtpVerification.interfaces";
import {
    generateUserDetailsUpdateOTP,
    verifyUserDetailsUpdateOTP,
} from "./UserDetails.api.calls";

// TODO: replace "case_n" with appropriate value
// So that a single variable can tell the difference in conditions
const MODAL_CASE_ENUM = {
    asking_booking_questions: "asking_booking_questions",
    case_2: "case_2",
    case_3: "case_3",
    get_in_touch: "get_in_touch",
    case_4: "case_4",
    case_5: "case_5",
    case_6: "case_6",
    case_7: "case_7",
};

// @dev - Telegram auth flow removal commit - fa2a084275141e0099ed4a7104961e98cacef609
const UserDetails = ({
    onClickBack,
    onClickContinue,
    isShownInterest: isShownInterestProp,
    isBooking = false,
    btnPressed,
    handleLogin,
    hasToShowFullAddress,
    isEventPage = false,
    isValidMerchandise, // This is enabled only for valid merchandise purchases
    closeModal,
    // modalInfo,
    isCloseable = true,
    directBookingDiscount,
    formDetailsProp,
    showGstOption,
    billingStateNonMandatory,
    userFlow,
    questionSourcesProp, // used when multiple listings are in the cart and you want to ask all the custom questions at once
    includesAutoDebitListing,
}: IUserDetails.IProps): JSX.Element => {
    const { notify } = useToastMessage();
    // isCloseable = true, // Whether this modal should be allowed to close on click out
    // const isCloseable = modalInfo?.isCloseable || true;
    const dispatch = useDispatch();
    const provider = useOrderBumps();
    const { showOrderBumps, setShowOrderBumps, shouldShowUpsellListings } =
        provider;

    const {
        hostName,
        dataHost: { profile_data: hostData },
    }: IHost.IStore = useSelector(SELECTORS.host);
    const isEmailMandatory = !hostData?.is_email_non_mandatory_for_booking;
    const {
        is_customer_details_updation_allowed: isCustomerDetailsUpdationAllowed,
    } = hostData ?? {};

    const {
        selectedEvent,
        paymentInfo: {
            answers: eventAnswers,
            numberOfCustomers: multipleUsersCount,
            thumbnails,
        },
        list: eventList,
    }: IEvent.IStore = useSelector(SELECTORS.event);

    const leadAnswers = useSelector(SELECTORS.leadAnswers);

    // * NOTE: For booking questions, we have support for multiple users but not so(yet) for lead-gen
    // questions, however that is a requirement that one can foresee; so for the sake of keeping the
    // structure of the `answers` as standard, the lead-gen answers are currently being stored at
    // the index=1 of the reference that is passed to this component (as {1: answers} instead of answers),
    // even if that is not so in the redux store structure. This has been done to minimise the (inevitable)
    // future effort for when multiple users might need to submit answers during lead-gen.

    const answers = isBooking ? eventAnswers : { 1: leadAnswers };

    const eventData: IEvent.IEventData | null =
        selectedEvent && isEventFetched(selectedEvent, eventList)
            ? eventList?.[selectedEvent]
            : null;
    const {
        title: offeringTitle,
        type: offeringType,
        metadata,
        allow_payment_method_toggle: allowPaymentMethodToggle,
    } = eventData ?? {};

    const { enable_multi_user_flow: enableMultipleUserCountFlow = false } =
        metadata ?? {};

    // incase the multiple user count flow is enabled, then instead of total customer details, we only take details
    // of the first user, and send the total count of the customers in api
    const numberOfCustomers = enableMultipleUserCountFlow
        ? 1
        : multipleUsersCount;

    const isInternationalCreator = !!hostData?.is_international_creator;
    const customHeader: string | undefined =
        (isEventPage && eventData?.custom_name_header) ||
        hostData?.custom_name_header;

    const isAutoDebitActiveOnMainListing = isAutoDebitActiveOnMainListingData(
        eventData!
    );
    const { isProcessing, startProcessing, stopProcessing } =
        useProcessing(btnPressed);

    // Count of number of customers for whom this will need to be collected
    const [detailsCount, setDetailsCount] = useState<number>(1);
    // Whether the current screen is for collecting answers
    const [isShowingQuestions, setShowingQuestions] = useState<boolean>(false); // Whether the form is currently in a processing state
    const [isShowingQnError, setShowingQnError] = useState<boolean>(false); // Whether the form is currently in a processing state

    const [isTnCChecked, setTnCChecked] = useState<boolean>(false); // Whether the form is currently in a processing state
    const [isGstChecked, setIsGstChecked] = useState<boolean>(false); // Whether the user has opted to fill GST details
    const [availabilityApiResponseData, setAvailabilityApiResponseData] =
        useState<IAvailabilityApiResponse["data"]>();
    const [otpError, setOtpError] = useState<string>();
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);

    const [requestId, setRequestId] = useState<string>();

    const [
        isShowingCredsAlreadyExists,
        showCredsAlreadyExists,
        hideCredsAlreadyExists,
    ] = useToggleState(false);
    const [
        isShowingAccountUpdateOtpVerification,
        showAccountUpdateOtpVerification,
        hideAccountUpdateOtpVerification,
    ] = useToggleState(false);

    const [credsAlreadyExistChoice, setCredsAlreadyExistChoice] = useState(
        CREDS_ALREADY_EXISTS_CHOICES.PROCEED_WITH_ALREADY_EXISTING_DETAILS
    );
    const [accountDetailsUpdateOtp, setAccountDetailsUpdateOtp] = useState("");

    const handleAccountDetailsUpdateOtpChange: IAccountUpdateOtpVerification["onChange"] =
        otp => {
            if (NUMBER_REGEX.test(otp)) {
                setAccountDetailsUpdateOtp(otp);
                setOtpError(undefined);
            }
        };

    const handleCredsAlreadyExistOptionChange: IConfirmCredsChange.IProps["onChange"] =
        value => {
            setCredsAlreadyExistChoice(value as number);
        };

    const {
        modalState: rebookingModalState,
        handleRebookingError,
        closeModal: closeRebookingModal,
    } = useRebookingConfirmationModalContainer();

    const formDetailsCollection: IUser.IStore["formDetails"] = useSelector(
        SELECTORS.formDetailsCollection
    );

    const isTelegram = eventData?.type === EVENT_TYPE.TELEGRAM;

    // * The values here might be overwritten in the code below depending on modal_case
    let formFieldOverrides: IUserDetails.IFormFieldOverrides = {
        [FORM_FIELDS.name]: {
            label: customHeader,
        },

        [EXTRA_FIELD.payment_method]: {
            ...((isAutoDebitActiveOnMainListing ||
                includesAutoDebitListing) && {
                // Override data key only if isAutodebit is active
                data: AUTO_DEBIT_PAYMENT_OPTION.map(v => ({
                    label: v,
                    value: v,
                })),
            }),
            hidden:
                /**
                // Note: Ideally we want to hide payment method selection when creator has custom stripe key.
                // But in case of SSR'ing the modal, the code to add the field in formFields runs after the
                // useEffect that sets the initial value of the form.
                //
                // Did not have bandwidth at that time to fix this properly (by setting the default value at
                // the right time), so we went ahead with showing the field to all international customers
                // and putting the onus on the user to select the method manually.

                // commented code starts
                getCustomStripeKey(hostData) || // If the creator has custom stripe key,
                // we need to disable the payment method dropdown selector.
                // As we want the payments to go to the stripe gateway in this case.
                // commented code ends
                */

                detailsCount > 1,
        },
    };

    const isShownInterest: boolean = !!(isShownInterestProp || null);

    const cartData = useMemo(getCartData, []);
    const cartListingsArr: string[] = useMemo(
        () => Object.keys(cartData.listingMap),
        [cartData.listingMap]
    );
    // Check id gstState is not inapplicable
    let gstState = eventData?.gst_state;
    if (userFlow === "cart") {
        const chosenListingUuid = cartListingsArr.find(listingUuid => {
            const _gstState =
                cartData.listingMap[listingUuid]?.eventData?.gst_state;
            return (
                _gstState === GST_STATUS.INCLUSIVE ||
                _gstState === GST_STATUS.EXCLUSIVE
            );
        });
        gstState = chosenListingUuid
            ? cartData.listingMap[chosenListingUuid]?.eventData?.gst_state
            : undefined;
    }
    // We show gst fields only if gst is enabled, user is domestic,
    // an gst state is 'not inapplicable'.
    // We don;t just check for 'gstState === GST_STATUS.INAPPLICABLE' for the
    // case where gstState might come undefined or null
    const isGSTStateNotInapplicable: boolean =
        gstState === GST_STATUS.INCLUSIVE || gstState === GST_STATUS.EXCLUSIVE;

    const baseFormFields: Array<string> = getBaseFormFields({
        isBooking,
        isValidMerchandise,
        detailsCount,
        hasToShowFullAddress,
        isInternationalCreator,
        isGSTStateNotInapplicable,
        currency:
            userFlow === "cart"
                ? getCartData().currency.currencyString
                : eventData?.currency_name,
    });

    const gstFormfields: Array<string> = [];
    if (isBooking && showGstOption && isGstChecked) {
        gstFormfields.push(EXTRA_FIELD.gst_info_gst_name);
        gstFormfields.push(EXTRA_FIELD.gst_info_gst_number);
        if (hostData?.einvoice_enabled) {
            gstFormfields.push(EXTRA_FIELD.gst_info_address);
            gstFormfields.push(EXTRA_FIELD.gst_info_pincode);
        }
    }

    const formfields = Array.from(
        new Set([...baseFormFields, ...gstFormfields].filter(v => v))
    );

    const formKey: string = getUserDetailsFormKey(detailsCount);
    // User details form data
    const {
        formDetails,
        setFormDetails,
        valid: isFormValid,
    }: IFormDetails.IReturnType = useFormDetails(formfields, formKey);

    const handleGstCheck = (): void => {
        const newIsGstChecked = !isGstChecked;
        setIsGstChecked(newIsGstChecked);

        setFormDetails([
            ...[...gstFormfields, EXTRA_FIELD.billing_state].map(f => {
                const field = formDetails?.[f] || {};
                const fieldProps =
                    formDetailsProp?.[
                        getUserDetailsFormKey(detailsCount)
                    ]?.find(fp => fp.key === f) || {};
                // @ts-ignore
                const value = field.value ?? fieldProps.value ?? "";

                return {
                    key: f,
                    value,
                    showError: field.showError ?? false,
                    error:
                        FORM_ENUM?.[f]?.validate?.(value, {
                            isInIndia: isIndia(
                                formDetails[FORM_FIELDS.country]?.value || INDIA
                            ),
                            billingStateNonMandatory,
                            isGstChecked: newIsGstChecked,
                            isInternationalCreator,
                            isTelegram,
                            formDetails,
                            isEmailMandatory,
                        }) ??
                        EXTRA_VALIDATION(formDetailsCollection, formKey)?.[f]?.(
                            value,
                            { isGstChecked: newIsGstChecked }
                        ) ??
                        null,
                    // @ts-ignore
                    editingDisabled: fieldProps.editingDisabled ?? false,
                };
            }),
        ]);
    };

    const setAnswers = (
        index: number,
        newAnswers: {
            [k: string]: string | Array<string>;
        }
    ): void => {
        dispatch(
            isBooking
                ? createAction(ACTION_TYPES.EVENT.UPDATE_BOOKING_DATA, {
                      answers: {
                          ...answers,
                          [index]: newAnswers,
                      },
                  })
                : createAction(ACTION_TYPES.USER.UPDATE_ANSWERS, {
                      answers: {
                          ...answers[1], // Refer to the note above the declaration of `answers` variable
                          ...newAnswers,
                      },
                  })
        );
    };

    const setThumbnails = (
        index: number,
        newThumbnails: {
            [k: string]: string;
        }
    ): void => {
        dispatch(
            isBooking
                ? createAction(ACTION_TYPES.EVENT.UPDATE_BOOKING_DATA, {
                      thumbnails: {
                          ...thumbnails,
                          [index]: newThumbnails,
                      },
                  })
                : createAction(ACTION_TYPES.USER.UPDATE_ANSWERS, {
                      thumbnails: {
                          ...thumbnails?.[1],
                          ...newThumbnails,
                      },
                  })
        );
    };

    const postAnalyticalEvent = (eventDetails: Object): void => {
        const screenName =
            (isShownInterest &&
                ((isEventPage && "shownInterestEventPage") ||
                    "shownInterest")) ||
            (isEventPage && "contactCollectionScreenEvenPage") ||
            "contactCollectionScreen";

        dispatch(
            createAction(ACTION_TYPES.UTILS.POST_ANALYTICS, {
                ...eventDetails,
                screen: screenName,
                funnel: "mypage",
                flow: "mypage",
                event_meta: eventData?.uuid,
            })
        );
    };

    const customTnC = eventData?.custom_t_c || hostData?.custom_t_c;

    const hasCustomTnC: boolean = !!(isBooking && customTnC);
    const needsToAcceptCustomTnC = hasCustomTnC && !isTnCChecked;

    const isSubmitDisabled: boolean =
        !isFormValid || isProcessing || needsToAcceptCustomTnC;

    // Question Source, contains the lead questions for express interest
    let questionSource = hostData?.leads_q_and_a || [];

    // incase add to cart flow,
    if (userFlow === "cart") {
        // we do not show lead questions in a booking flow,
        // so we replace the lead questions, with the booking
        // questions of all the listings in the cart
        questionSource = [];
        questionSourcesProp?.forEach(qs => {
            questionSource = [...questionSource, ...qs.q_and_a];
        });
        // we filter the questions to show the before booking questions only in booking form
        questionSource = questionSource.filter(item => {
            return (
                item.question_placement === QUESTION_PLACEMENT.BEFORE_BOOKING
            );
        });
        // else, we fill it with custom questions of the listing
    } else if (isBooking) {
        questionSource = eventData?.q_and_a || [];
        questionSource = questionSource.filter(item => {
            return (
                item.question_placement === QUESTION_PLACEMENT.BEFORE_BOOKING
            );
        });
    }

    const filteredQuestions = [...questionSource];

    // Whether to show the custom questions on the same page or not
    // @ts-ignore
    const askQuestions: boolean = !!(!isEmpty(filteredQuestions) ?? false);

    // Whether to show the custom questions on the same page or not.
    // This is only applicable for booking questions, not lead-gen questions
    let showInSameScreen: boolean = true;
    if (userFlow === "cart") {
        showInSameScreen = false;
    } else if (askQuestions && isBooking) {
        showInSameScreen = eventData?.metadata?.q_and_a_same_popup ?? false;
    }

    const isShowingAnswersCurrently: boolean =
        askQuestions && (showInSameScreen ? true : isShowingQuestions);

    // Whether the page is at the final page?
    let finalPage: boolean = false;
    if (userFlow === "cart") {
        finalPage = askQuestions ? isShowingQuestions : true;
    } else if (isBooking) {
        if (isTelegram) {
            formFieldOverrides[FORM_FIELDS.phone as IUserDetails.IFormKey] = {
                label: "Telegram Number",
                icon: MEDIA.ICONS.CALL_PLATFORMS.telegram,
            };
        }
        const isFinalUser = numberOfCustomers <= detailsCount;
        if (askQuestions) {
            finalPage =
                isFinalUser && (showInSameScreen ? true : isShowingQuestions);
        } else {
            finalPage = isFinalUser;
        }
    }
    if (isShowingCredsAlreadyExists || isShowingAccountUpdateOtpVerification)
        finalPage = true;

    const handlePaymentAvailabilitySuccess: IAction.IPayloadProps["successCallback"] =
        response => {
            const availabilityResponse =
                response.response as IAvailabilityApiResponse;

            if (
                isCustomerDetailsUpdationAllowed &&
                !isShowingAccountUpdateOtpVerification &&
                availabilityResponse.data?.has_user_details_changed
            ) {
                stopProcessing();
                showCredsAlreadyExists();
                setShowingQuestions(false);
                setShowOrderBumps(false);
                setAvailabilityApiResponseData(availabilityResponse?.data);
            }
        };

    const handleProceedToUpdateAccountDetails = () => {
        if (requestId) {
            startProcessing();
            setIsSendingOtp(true);
            generateUserDetailsUpdateOTP({ requestId })
                .then(() => {
                    hideCredsAlreadyExists();
                    showAccountUpdateOtpVerification();
                })
                .catch(error => {
                    notify(error?.message, { type: toast_type_keys.error });
                    logError({
                        error,
                        when: "calling generateUserDetailsUpdateOTP",
                        severity: "blocker",
                        occuredAt:
                            "src/components/modals/components/UserDetails/index.tsx",
                    });
                })
                .finally(() => {
                    stopProcessing();
                    setIsSendingOtp(false);
                });
        }
    };

    const handleVerifyOtpToUpdateAccountDetails = () => {
        if (checkIsStringEmpty(accountDetailsUpdateOtp)) {
            notify("Please enter the OTP", { type: toast_type_keys.error });
            setOtpError("Please enter the OTP");
            return;
        }

        if (requestId) {
            startProcessing();
            setIsVerifyingOTP(true);
            verifyUserDetailsUpdateOTP({
                otp: accountDetailsUpdateOtp,
                requestId,
            })
                .then(() =>
                    handleFormSubmit(undefined, {
                        isUserDetailsUpdateOtpVerificationSuccessful: true,
                    })
                )
                .catch(error => {
                    notify(error?.message, { type: toast_type_keys.error });
                    setOtpError("Wrong OTP");
                    logError({
                        error,
                        when: "calling handleVerifyOtpToUpdateAccountDetails",
                        occuredAt:
                            "src/components/modals/components/UserDetails/index.tsx",
                        severity: "blocker",
                    });
                })
                .finally(() => {
                    stopProcessing();
                    setIsVerifyingOTP(false);
                });
        }
    };

    const handleLoginSuccess = (k: IFetchDataAction.IReturnPayload) => {
        const responseRequestId = k?.response?.data?.request_id;
        setRequestId(responseRequestId);
    };

    const handleFormSubmit = (
        e?: React.FormEvent<HTMLFormElement>,
        extraData: {
            telegramInfo?: IUserDetails.ITelegramInfoState;
            isUserDetailsUpdateOtpVerificationSuccessful?: boolean;
        } = {}
    ): void => {
        if (e) e.preventDefault();

        let { isUserDetailsUpdateOtpVerificationSuccessful } = extraData ?? {};

        if (!isUserDetailsUpdateOtpVerificationSuccessful) {
            // send otp
            if (
                isShowingCredsAlreadyExists &&
                isProceedToUpdateAccountDetails(credsAlreadyExistChoice)
            ) {
                handleProceedToUpdateAccountDetails();
                return;
            }

            // proceed with existsing account details
            if (
                isShowingCredsAlreadyExists &&
                isProceedWithExistingAccountDetails(credsAlreadyExistChoice)
            ) {
                isUserDetailsUpdateOtpVerificationSuccessful = true; // this option doesn't need any otp verification
            }

            // verify otp to update account details
            if (isShowingAccountUpdateOtpVerification) {
                handleVerifyOtpToUpdateAccountDetails();
                return;
            }
        }

        if (isSubmitDisabled) {
            if (isShowingAnswersCurrently) {
                setShowingQnError(true);
            }
            setFormDetails(
                Object.values(formfields)
                    // country code and country will always be valid fields
                    .filter(
                        f =>
                            ![
                                FORM_FIELDS.countryCode,
                                FORM_FIELDS.country,
                            ].includes(f)
                    )
                    .map((f: string) => ({
                        key: f,
                        showError: true,
                    }))
            );
            if (isProcessing) return;
            alert(
                chainShortCircuits<string>([
                    [!isFormValid, "Please fill the form with valid details"],
                    [
                        needsToAcceptCustomTnC,
                        "Please read and accept the Terms and Condition",
                    ],
                ])
            );
            return;
        }
        if (askQuestions) {
            setShowingQnError(false);

            if (isShowingAnswersCurrently) {
                setShowingQnError(true);
                const data = filteredQuestions;
                for (let i = 0; i < data.length; i += 1) {
                    if (data[i].is_mandatory) {
                        const userAnswers = answers?.[detailsCount] || {};
                        const userInput = userAnswers[data[i].uuid];
                        const errorCaught = CUSTOM_QUESTION_TYPES[data[i].type]
                            ? CUSTOM_QUESTION_TYPES[
                                  data[i].type
                              ].validateAnswer(userInput)
                            : false;

                        if (errorCaught) {
                            alert("Please fill the appropriate answers");
                            // ToDo : Ideally should be specific alerts for specific validation fails
                            return;
                        }
                    }
                }
            }
        }

        const finalFormData: {
            email: string;
            full_name: string;
            phone_number: string;
            country_code: string;
            [k: string]: string;
        }[] = Object.entries(formDetailsCollection)
            /**
             .filter(([k]) => isUserDetailFormKey(k))
             reason for using this condition:
             formDetails reducer can have multiple forms stored
             we need to check only those which are UserDetailsForm
             */
            .filter(([k]) => isUserDetailFormKey(k))
            .map(([, formDetailsItem]) =>
                Object.assign(
                    {},
                    ...Object.values(formDetailsItem).map(v => {
                        if (!isBooking && v.key === FORM_FIELDS.phone) {
                            // Send Host Message needs a "phone_number" but Payment Login needs a "phone" field name
                            return {
                                phone_number: v.value ? v.value.trim() : null,
                            };
                        }
                        if (
                            !(isGstChecked || !billingStateNonMandatory) &&
                            EXTRA_FIELD.billing_state === v.key &&
                            isEmpty((v.value || "").trim())
                        ) {
                            // in case billing_state is not set, backend assigns the default value
                            // so don't send this key
                            return {};
                        }
                        if (v.key === FORM_FIELDS.phone) {
                            // if phone number not provided for international users during booking,
                            // the value of phone to be sent as null
                            return {
                                [v.key]: v.value ? v.value.trim() : null,
                            };
                        }
                        return {
                            [v.key]: (v.value || "").trim(),
                        };
                    })
                )
            );

        if (isBooking && handleLogin) {
            if (!finalPage && !showOrderBumps) {
                if (askQuestions && !showInSameScreen) {
                    if (isShowingQuestions) {
                        setDetailsCount(detailsCount + 1);
                        setShowingQuestions(false);
                    } else {
                        setShowingQuestions(true);
                    }
                } else {
                    setDetailsCount(detailsCount + 1);
                }
                return;
            }

            const shouldShowOrderBumps =
                shouldShowUpsellListings &&
                !(
                    isShowingCredsAlreadyExists ||
                    isShowingAccountUpdateOtpVerification
                );

            if (shouldShowOrderBumps) {
                setShowingQuestions(false);
                return setShowOrderBumps(true);
            }

            postAnalyticalEvent({
                event_name: "continue1",
                action: "Tap",
                ui_component: "Button",
                identifier: "Continue",
            });

            handleLogin({
                customerDetailsProp: finalFormData,
                isUserDetailsUpdateOtpVerificationSuccessful,
                renewBookingCallback: handleRebookingError,
                successCallback: handleLoginSuccess,
                paymentAvailabilitySuccessCallback:
                    handlePaymentAvailabilitySuccess,
                // if multiple user count flow is enabled, then we need to send the head count as well
                ...(enableMultipleUserCountFlow && {
                    headCount: multipleUsersCount,
                }),
            });
            return;
        }
        const utmDetails = getUtmParams();
        /**
         * Fetching user IP details required for sending in Payload for analytics
         */
        const analyticsInfo = getAnalyticsInfo();

        const urlParams = new URLSearchParams(window.location.search);

        const payload = {
            ...finalFormData[0],
            sub_domain: hostName,
            listing_id: eventData?.id || "",
            analytics_info: analyticsInfo,
            log_type: FORM_LOG_TYPES.GET_IN_TOUCH,

            ...(urlParams.get(URL_SEARCH_PARAMS.DYNAMIC_LINK) && {
                dynamic_link_id: urlParams.get(URL_SEARCH_PARAMS.DYNAMIC_LINK),
            }),
            ...(!isBooking &&
                askQuestions && {
                    // Refer to the note above the declaration of `answers` variable
                    lead_answers: processCustomQuestions(
                        questionSource || [],
                        answers[1]
                    ),
                }),
            ...(window.location.search && { utm_info: utmDetails }),
        };
        startProcessing();

        dispatch(
            createAction(ACTION_TYPES.UTILS.API_CALL, {
                apiActionType: API_ACTION_TYPES.SEND_HOST_MESSAGE,
                payload,
                successCallback: () => {
                    alert("Your message has been successfully sent.");
                    postAnalyticalEvent({
                        event_name: "continue1",
                        action: "Tap",
                        ui_component: "Button",
                        identifier: "Continue",
                    });
                    const postInterestURL: string | undefined =
                        eventData?.metadata?.post_interest_url ||
                        hostData?.post_interest_url;
                    if (postInterestURL) {
                        window.open(postInterestURL, "_blank");
                    }
                    (onClickContinue || closeModal)();
                },
                errorCallback: ({ response: { message: messageProp } }) => {
                    alert(
                        `Error: ${
                            messageProp ||
                            "An error occurred in sending the message. Please try again."
                        }`
                    );
                    postAnalyticalEvent({
                        event_name: "error",
                        action: "Tap",
                        ui_component: "Button",
                        identifier: "Continue",
                    });
                },
                finallyCallback: () => {
                    stopProcessing();
                    if (isBrowser()) {
                        postAnalytics(ANALYTICS_EVENTS.CONTACT, {
                            sub_domain: hostName,
                            event_source_url: window.location.href,
                            content_name: offeringTitle,
                            content_category: offeringType
                                ? EVENT_ENUM[offeringType].TITLE
                                : undefined,
                            currency: eventData?.currency_name,
                            value: eventData?.price_per_head,
                            user_data: {
                                em: payload.email,
                                name: payload?.full_name,
                                ph: `${payload?.country_code ?? ""}${
                                    payload?.phone_number ?? ""
                                }`,
                            },
                        });
                    }
                },
            })
        );
    };

    const isSendingHostMessage =
        useFetchStatus(API_ACTION_TYPES.SEND_HOST_MESSAGE) ===
        FETCH_STATUS.FETCHING;
    const isAuthenticatingUser =
        useFetchStatus(API_ACTION_TYPES.PAYMENT_AUTH) === FETCH_STATUS.FETCHING;
    const isCheckingAvailability =
        useFetchStatus(API_ACTION_TYPES.PAYMENT_AVAILABILITY) ===
        FETCH_STATUS.FETCHING;
    const isInitiatingLogin =
        useFetchStatus(API_ACTION_TYPES.PAYMENT_INITIATE) ===
        FETCH_STATUS.FETCHING;
    const isRedirectingToPaymentGateway =
        useFetchStatus(API_ACTION_TYPES.PAYMENT_INITIATE) ===
        FETCH_STATUS.FETCHED;

    const isProcessingFinalFlag =
        isProcessing ||
        isSendingOtp ||
        isVerifyingOTP ||
        isSendingHostMessage ||
        isAuthenticatingUser ||
        isCheckingAvailability ||
        isInitiatingLogin;

    // TODO: Custom hook
    const processingStatus: string = chainShortCircuits<string>(
        [
            [isSendingOtp, "Sending OTP"],
            [isVerifyingOTP, "Verifying OTP"],
            [isSendingHostMessage, "Sending Message"],
            [isAuthenticatingUser, "Authenticating User"],
            [isCheckingAvailability, "Checking Availability"],
            [isInitiatingLogin, "Initiating Login"],
            [isRedirectingToPaymentGateway, "Redirecting to Payment Gateway"],
        ],
        "Processing"
    );

    // Text for the CTA button
    let ctaText: string = chainShortCircuits<string>(
        [
            [
                isShowingCredsAlreadyExists &&
                    isProceedToUpdateAccountDetails(credsAlreadyExistChoice),
                "Send OTP for verification",
            ],
            [isShowingAccountUpdateOtpVerification, "Verify"],
            [showOrderBumps, "Initiate Booking"],
            [!isBooking, "Send Message"],
            [
                isBooking && finalPage,
                eventData?.metadata?.popup_initiate_booking_cta_text ??
                    "Initiate Booking",
            ],
            // TODO: See if these values are needed
            // eventData?.metadata?.popup_proceed_cta_text || "Proceed"
            [
                isBooking && !finalPage,
                showInSameScreen || (!showInSameScreen && isShowingQuestions)
                    ? `${
                          getUserDetailsFormKey(detailsCount + 1) in
                          formDetailsCollection
                              ? "Edit"
                              : "Add"
                      } details${
                          numberOfCustomers > 1
                              ? ` of user ${detailsCount + 1}`
                              : ""
                      }`
                    : `Enter Answers${
                          numberOfCustomers > 1
                              ? ` for user ${detailsCount}`
                              : ""
                      }`,
            ],
        ],
        "Continue"
    );

    const modalCase = chainShortCircuits<string>(
        // TODO: replace "case_n" with appropriate value
        // and add switch cases and do manipulation there.
        // So that a single variable can tell the difference in conditions
        [
            [
                // isBooking && isQnANeeded && isShowingQuestions,
                isBooking && askQuestions && isShowingQuestions,
                MODAL_CASE_ENUM.asking_booking_questions,
            ],
            [
                // For when the modal is opened for booking
                isBooking && eventData?.metadata?.user_details?.title,
                MODAL_CASE_ENUM.case_2,
            ],
            // For when the modal is opened from anywhere
            [
                !isBooking && hostData?.user_details?.title,
                MODAL_CASE_ENUM.case_3,
            ],
            [!isBooking, MODAL_CASE_ENUM.get_in_touch],
            [
                isBooking && isValidMerchandise && detailsCount === 1, // Only show this on the first page
                MODAL_CASE_ENUM.case_4,
            ],
            [isBooking && detailsCount > 1, MODAL_CASE_ENUM.case_5],
            [
                isBooking && detailsCount === 1, // Only show this on the first page
                MODAL_CASE_ENUM.case_6,
            ],
        ],
        MODAL_CASE_ENUM.case_7
    );

    let titleText: string = chainShortCircuits<string>(
        [
            [isShowingAccountUpdateOtpVerification, "OTP Verification"],
            [isShowingCredsAlreadyExists, "Confirm account details"],
            [showOrderBumps, "Booking Summary"],
            [
                // isBooking && isQnANeeded && isShowingQuestions,
                isBooking && askQuestions && isShowingQuestions,
                "Answer the following questions",
            ],
            [
                // For when the modal is opened for booking
                isBooking && eventData?.metadata?.user_details?.title,
                eventData?.metadata?.user_details?.title,
            ],
            // For when the modal is opened from anywhere
            [
                !isBooking && hostData?.user_details?.title,
                hostData?.user_details?.title,
            ],
            [
                isBooking && isValidMerchandise && detailsCount === 1, // Only show this on the first page
                "Shipping/Delivery details",
            ],
            [isBooking && detailsCount > 1, `Details of user ${detailsCount}`],
            [
                isBooking && detailsCount === 1, // Only show this on the first page
                "Where do we contact you?",
            ],
        ],

        "Where do we contact you?"
    );

    switch (modalCase) {
        case MODAL_CASE_ENUM.get_in_touch:
            titleText =
                hostData?.custom_get_in_touch_form_title || "Get in touch";
            formFieldOverrides = {
                [FORM_FIELDS.name]: {
                    label: hostData?.custom_get_in_touch_form_field_name,
                },
                [FORM_FIELDS.email]: {
                    label: hostData?.custom_get_in_touch_form_field_email,
                },
                [FORM_FIELDS.phone]: {
                    label: hostData?.custom_get_in_touch_form_field_phone,
                },
                [EXTRA_FIELD.message]: {
                    label: hostData?.custom_get_in_touch_form_field_message,
                },
            };
            ctaText = hostData?.custom_get_in_touch_form_cta || "Send Message";
            break;
        case MODAL_CASE_ENUM.asking_booking_questions:
        case MODAL_CASE_ENUM.case_2:
        case MODAL_CASE_ENUM.case_3:
        case MODAL_CASE_ENUM.case_4:
        case MODAL_CASE_ENUM.case_5:
        case MODAL_CASE_ENUM.case_6:
        case MODAL_CASE_ENUM.case_7:
            break;
        default:
            break;
    }

    const countryCode =
        useSelector((state: IStore.IState) => state.host.country.code) || "IN";
    const DEFAULT_COUNTRY: ICountryCodeDropdownOption =
        getCountryCodeDropdownOptionFromKey(countryCode || "IN");

    useEffect(() => {
        loadStripePaymentGateway();
        if (askQuestions) {
            // Clear the stored answers if any
            dispatch(
                createAction(ACTION_TYPES.EVENT.UPDATE_BOOKING_DATA, {
                    answers: {},
                })
            );
        }
        return () => {
            setFormDetails();
            setShowingQnError(false);
            if (!isBooking) {
                dispatch(
                    createAction(ACTION_TYPES.UTILS.UPDATE_API_CALL_STATUS, {
                        [API_ACTION_TYPES.SEND_HOST_MESSAGE]:
                            FETCH_STATUS.NOT_FETCHED,
                    })
                );
                dispatch(
                    createAction(ACTION_TYPES.USER.UPDATE_ANSWERS, {
                        answers: {},
                    })
                );
            } else {
                // Clear the stored user form details
                Array.from(Array(numberOfCustomers)).forEach((x, i) => {
                    const key: string = getUserDetailsFormKey(i + 1);
                    dispatch(
                        createAction(ACTION_TYPES.USER.CLEAR_FORM_DETAILS, {
                            key,
                        })
                    );
                });
            }
        };
    }, []);

    // TODO: Create a hook to do this automatically
    useEffect(() => {
        setShowingQnError(false);

        // We're storing both country name and dial code, as it is needed by the API.
        // In the future, we can just store one value and transform it into two
        // corresponding values right before the API call is made
        setFormDetails([
            {
                key: FORM_FIELDS.country,
                value:
                    formDetails?.[FORM_FIELDS.country]?.value ??
                    DEFAULT_COUNTRY.name,
                showError: false,
                error: "",
            },
            {
                key: FORM_FIELDS.countryCode,
                value:
                    formDetails?.[FORM_FIELDS.country]?.value ??
                    DEFAULT_COUNTRY.dial_code,
                showError: false,
                error: "",
            },
            ...formfields
                .filter(
                    f =>
                        ![
                            FORM_FIELDS.countryCode,
                            FORM_FIELDS.country,
                        ].includes(f)
                )
                .map((f: string) => {
                    const field = formDetails?.[f] || {};
                    const fieldProps: IUser.IFormDataVal | {} =
                        formDetailsProp?.[
                            getUserDetailsFormKey(detailsCount)
                        ]?.find(fp => fp.key === f) || {};

                    // @ts-ignore
                    const value =
                        field.value ??
                        ("value" in fieldProps ? fieldProps?.value : null) ??
                        FORM_ENUM[f].initialValue ??
                        "";

                    return {
                        key: f,
                        value,
                        showError: field.showError ?? false,
                        error:
                            FORM_ENUM?.[f]?.validate?.(value, {
                                isInIndia: isIndia(
                                    formDetails[FORM_FIELDS.country]?.value ||
                                        INDIA
                                ),
                                billingStateNonMandatory,
                                isGstChecked,
                                isInternationalCreator,
                                isTelegram,
                                formDetails,
                                isEmailMandatory,
                            }) ??
                            EXTRA_VALIDATION(formDetailsCollection, formKey)?.[
                                f
                            ]?.(value, {
                                isGstChecked,
                            }) ??
                            null,
                        // @ts-ignore
                        editingDisabled: fieldProps.editingDisabled ?? false,
                    };
                }),
        ]);
        if (askQuestions && !(detailsCount in answers)) {
            setAnswers(
                detailsCount,
                Object.assign(
                    {},
                    ...Object.values(filteredQuestions).map(q =>
                        CUSTOM_QUESTION_TYPES[q.type]
                            ? {
                                  [q.uuid]:
                                      CUSTOM_QUESTION_TYPES[q.type]
                                          .defaultAnswerValue,
                              }
                            : {}
                    )
                )
            );
        }
    }, [detailsCount]);

    useEffect(() => {
        if (isInternationalCreator && !isTelegram) {
            setFormDetails([
                {
                    key: FORM_FIELDS.phone,
                    value: null,
                    showError: false,
                    error: "",
                },
            ]);
        }
    }, [isInternationalCreator]);

    usePrefillBookingFormValues({ isBooking, setFormDetails });

    const handleCloseUserDetailsAlreadyExistsView = () => {
        hideCredsAlreadyExists();
        setCredsAlreadyExistChoice(
            CREDS_ALREADY_EXISTS_CHOICES.PROCEED_WITH_ALREADY_EXISTING_DETAILS
        );
    };

    const handleCloseAccountDetailsUpdateOtpVerification = () => {
        hideAccountUpdateOtpVerification();
        showCredsAlreadyExists();
        setAccountDetailsUpdateOtp("");
        setOtpError(undefined);
    };

    // Back button functionality
    const handleBack = (): void => {
        if (showOrderBumps) {
            return setShowOrderBumps(false);
        }

        if (isShowingCredsAlreadyExists) {
            handleCloseUserDetailsAlreadyExistsView();
        }

        if (isShowingAccountUpdateOtpVerification) {
            handleCloseAccountDetailsUpdateOtpVerification();
        }

        if (askQuestions && !showInSameScreen) {
            if (isShowingQuestions) {
                setShowingQuestions(false);
            } else if (detailsCount > 1) {
                setDetailsCount(Math.max(detailsCount - 1, 1));
                setShowingQuestions(true);
            }
        } else {
            setDetailsCount(Math.max(detailsCount - 1, 1));
        }
    };

    const handleTnCChange = (): void => {
        setTnCChecked((current: boolean) => !current);
    };

    // Props for the <CustomQuestions /> component
    const customQuestionProps: ICustomQuestions.IProps = {
        data: filteredQuestions,
        answers: answers?.[detailsCount] || {},
        setAnswers: data => setAnswers(detailsCount, data),
        showError: isShowingQnError,
        showAfterBookingQuestions: false,
        setShowError: setShowingQnError,
        uploadSubfolder: (isBooking
            ? `question-listings/${eventData?.uuid}`
            : getLeadsFormFileUploadSubfolder())!,
        thumbnails: thumbnails?.[detailsCount] || {},
        setThumbnails: data => setThumbnails(detailsCount, data),
    };

    /* The boolean check here is just check if the page is not the very first shown */
    const isFirstPage: boolean =
        detailsCount === 1 &&
        (askQuestions && !showInSameScreen ? !isShowingQuestions : true) &&
        !isShowingCredsAlreadyExists &&
        !isShowingAccountUpdateOtpVerification;

    const renderBody = () => {
        if (isShowingQuestions && !showInSameScreen) {
            return <CustomQuestions {...customQuestionProps} />;
        } else if (showOrderBumps) {
            return <OrderBumps />;
        } else if (isShowingCredsAlreadyExists) {
            return (
                <ConfirmCredsChange
                    value={credsAlreadyExistChoice}
                    availabilityApiResponseData={availabilityApiResponseData}
                    onChange={handleCredsAlreadyExistOptionChange}
                />
            );
        } else if (isShowingAccountUpdateOtpVerification) {
            return (
                <AccountUpdateOtpVerification
                    otp={accountDetailsUpdateOtp}
                    error={otpError}
                    onChange={handleAccountDetailsUpdateOtpChange}
                />
            );
        } else {
            return (
                <>
                    <DetailsForm
                        isGstChecked={isGstChecked}
                        isValidMerchandise={isValidMerchandise}
                        formfields={baseFormFields}
                        formDetails={formDetails}
                        setFormDetails={setFormDetails}
                        postAnalyticalEvent={postAnalyticalEvent}
                        formFieldOverrides={formFieldOverrides}
                        // Use this instead of extraFormFields
                        formKey={formKey}
                        formDetailsCollection={formDetailsCollection}
                        billingStateNonMandatory={billingStateNonMandatory}
                        isInternationalCreator={isInternationalCreator}
                        isTelegram={isTelegram}
                        allowPaymentMethodToggle={Boolean(
                            allowPaymentMethodToggle
                        )}
                    />
                    {showInSameScreen ? (
                        <CustomQuestions {...customQuestionProps} />
                    ) : null}

                    {isBooking &&
                    isFirstPage &&
                    directBookingDiscount &&
                    eventData ? (
                        <DirectDiscount eventData={eventData} />
                    ) : null}

                    {isBooking && showGstOption ? (
                        <>
                            <div className={styles.formField}>
                                <label
                                    htmlFor="userInfo-gstCheck"
                                    className={classNames(
                                        styles.inputContainer,
                                        styles.tNc
                                    )}
                                >
                                    <div className="d-flex align-items-center">
                                        <input
                                            type="checkbox"
                                            name="gst"
                                            id="userInfo-gstCheck"
                                            checked={isGstChecked}
                                            onChange={handleGstCheck}
                                        />
                                    </div>
                                    <span className={styles.tncLabel}>
                                        I have GST number (India Only)
                                    </span>
                                </label>
                            </div>
                            <hr />
                        </>
                    ) : null}

                    {isBooking && showGstOption && isGstChecked ? (
                        <DetailsForm
                            isGstChecked={isGstChecked}
                            formfields={gstFormfields}
                            formDetails={formDetails}
                            setFormDetails={setFormDetails}
                            postAnalyticalEvent={postAnalyticalEvent}
                            formKey={formKey}
                            formDetailsCollection={formDetailsCollection}
                            billingStateNonMandatory={billingStateNonMandatory}
                            formFieldOverrides={formFieldOverrides}
                            isInternationalCreator={isInternationalCreator}
                            isTelegram={isTelegram}
                            isGSTStateNotInapplicable={
                                isGSTStateNotInapplicable
                            }
                            showGSTInfoNote
                            allowPaymentMethodToggle={Boolean(
                                allowPaymentMethodToggle
                            )}
                        />
                    ) : null}

                    {isBooking && hasCustomTnC && isFirstPage ? (
                        <div className={styles.formField}>
                            <label
                                htmlFor="userInfo-tnc"
                                className={classNames(
                                    styles.inputContainer,
                                    styles.tNc
                                )}
                            >
                                <div className="d-flex align-items-center">
                                    <input
                                        type="checkbox"
                                        name="tNc"
                                        id="userInfo-tnc"
                                        checked={isTnCChecked}
                                        onChange={handleTnCChange}
                                    />
                                </div>
                                <span className={styles.tncLabel}>
                                    I have read and agreed to the&nbsp;
                                    <a
                                        className={styles.tNcLink}
                                        href={customTnC || ""}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Terms and Conditions
                                    </a>
                                </span>
                            </label>
                        </div>
                    ) : null}

                    <div
                        className={`d-flex my-1 align-items-start ${styles.detailsMessage}`}
                    >
                        <img src={REFUND_ICON_SRC} alt="refundIcon" />
                        <div className={`ml-3 `}>
                            <div className={`${styles.paymentStatusHeader}`}>
                                Don’t worry, your details are safe.
                            </div>
                            {/* TODO: confirm with muskaan if its needed for all cases or just payment modal case */}
                            <div className={`${styles.paymentStatusText} mt-1`}>
                                We just need this information so that we can
                                share the event details with you once you book
                                successfully.
                            </div>
                        </div>
                    </div>
                </>
            );
        }
    };

    return (
        <>
            <form className={styles.root} onSubmit={handleFormSubmit}>
                <div className={styles.header}>
                    {!isFirstPage || showOrderBumps ? (
                        // eslint-disable-next-line react/jsx-indent
                        <div
                            className={styles.back}
                            title="Back"
                            onClick={handleBack}
                        >
                            <HiArrowLeft />
                        </div>
                    ) : null}
                    <div className={styles.title}>{titleText}</div>
                    {isCloseable ? (
                        <div
                            className={styles.close}
                            title="Close"
                            onClick={onClickBack || closeModal}
                        >
                            &times;
                        </div>
                    ) : null}
                </div>

                <div
                    onScroll={() => {
                        window.document
                            .querySelector("#payable_amount_strip")
                            ?.classList.add("d-flex");
                    }}
                    className={styles.formContainer}
                >
                    {renderBody()}
                </div>

                <div className={styles.ctaButtonContainer}>
                    {showOrderBumps && <TotalPayableStrip />}
                    <div
                        className={`${styles.ctaRow} d-flex justify-content-center align-items-center`}
                    >
                        <ButtonV2
                            type="submit"
                            className={styles.submitButton}
                            id="userInfo-ctaButton"
                            applyTheme
                            disabled={isProcessingFinalFlag}
                        >
                            {isProcessingFinalFlag ? (
                                <div
                                    className={classNames(
                                        "loader4",
                                        styles.ctaLoader
                                    )}
                                />
                            ) : null}
                            <div className="d-flex align-items-center">
                                {isProcessingFinalFlag
                                    ? processingStatus
                                    : ctaText}
                            </div>
                        </ButtonV2>
                    </div>
                </div>
            </form>

            <RebookingConfirmationModal
                {...rebookingModalState}
                onClose={closeRebookingModal}
            />
        </>
    );
};

export default UserDetails;
